import { isTouchOnlyDevice, useControlScheme } from '../../game/input/controlScheme';
import { ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from '../../game/cameraZoom';
import {
  mobileCameraZoom,
  useSettingsStore,
  type ControlScheme,
  type JoystickMode,
  type JoystickSize,
  type TapAction,
} from '../../store/settingsStore';
import { Segmented } from './Segmented';

const SCHEMES: { value: ControlScheme; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'mobile', label: 'Mobile' },
];

const TAP_ACTIONS: { value: TapAction; label: string }[] = [
  { value: 'autoAim', label: 'Auto-aim' },
  { value: 'facing', label: 'Facing direction' },
];

const JOYSTICK_MODES: { value: JoystickMode; label: string }[] = [
  { value: 'floating', label: 'Floating' },
  { value: 'fixed', label: 'Fixed' },
];

const toPercent = (zoom: number) => Math.round(zoom * 100);

const JOYSTICK_SIZES: { value: JoystickSize; label: string }[] = [
  { value: 's', label: 'S' },
  { value: 'm', label: 'M' },
  { value: 'l', label: 'L' },
];

/**
 * Options → Controls, touch part (DROW-176): the control scheme, and the Mobile options once it
 * resolves to Mobile. Applied live: the touch overlay switches without a reload, and mouse and
 * keyboard keep working whichever scheme is on.
 */
export function TouchOptions() {
  const s = useSettingsStore();
  const resolved = useControlScheme();
  const detected = isTouchOnlyDevice() ? 'touch screen' : 'mouse and keyboard';
  const zoom = toPercent(mobileCameraZoom(s));
  return (
    <>
      <Segmented
        label="Control scheme"
        options={SCHEMES}
        value={s.controlScheme}
        onChange={s.setControlScheme}
      />
      <p className="muted option-hint">
        {s.controlScheme === 'auto'
          ? `Auto: ${resolved === 'mobile' ? 'touch controls' : 'desktop controls'} (${detected} detected).`
          : 'Mouse and keyboard always work; Mobile adds the on-screen joystick and buttons.'}
      </p>
      {resolved === 'mobile' && (
        <>
          <h3 className="option-heading">Touch controls</h3>
          {/* Live, even mid-round: the camera eases to the new zoom (DROW-181). */}
          <label className="option-row">
            <span className="option-label">Camera zoom</span>
            <span className="option-slider">
              <input
                type="range"
                min={toPercent(ZOOM_MIN)}
                max={toPercent(ZOOM_MAX)}
                step={toPercent(ZOOM_STEP)}
                value={zoom}
                aria-valuetext={`${zoom}%`}
                onChange={(e) => s.setCameraZoomMobile(Number(e.target.value) / 100)}
              />
              <output>{zoom}%</output>
            </span>
          </label>
          <Segmented
            label="Tap to cast"
            options={TAP_ACTIONS}
            value={s.tapAction}
            onChange={s.setTapAction}
          />
          <Segmented
            label="Joystick"
            options={JOYSTICK_MODES}
            value={s.joystickMode}
            onChange={s.setJoystickMode}
          />
          <Segmented
            label="Joystick size"
            options={JOYSTICK_SIZES}
            value={s.joystickSize}
            onChange={s.setJoystickSize}
          />
          <label className="option-check">
            <input
              type="checkbox"
              checked={s.mirrorControls}
              onChange={(e) => s.setMirrorControls(e.target.checked)}
            />
            <span>
              Mirror controls
              <span className="muted option-hint">Joystick on the right, spells on the left.</span>
            </span>
          </label>
          <label className="option-check">
            <input
              type="checkbox"
              checked={s.haptics}
              onChange={(e) => s.setHaptics(e.target.checked)}
            />
            <span>
              Haptics
              <span className="muted option-hint">Vibrate on casts and big hits (Android).</span>
            </span>
          </label>
        </>
      )}
    </>
  );
}
